import { useUserProfileData } from "@/features/profile/hooks/useUserProfileData";
import { usePermissions } from "@/utils/permissions";
import { Navigate } from "react-router-dom";
import { ReactNode } from "react";
import { Loading } from "@/components/shared/ui/Loading";

interface RequirePermissionProps {
  children: ReactNode;
  permissionCheck: (permissions: ReturnType<typeof usePermissions>) => boolean;
  fallbackPath?: string;
}

/**
 * Component that wraps children and ensures the user passes the permission check.
 * If not, redirects to a fallback path.
 * 
 * @param children - The components to render if permission is granted
 * @param permissionCheck - Function that receives permissions object and returns true if access is allowed
 * @param fallbackPath - Path to redirect to if permission denied (default: "/")
 */
export function RequirePermission({ 
  children, 
  permissionCheck,
  fallbackPath = "/" 
}: RequirePermissionProps) {
  const { userProfile, isLoading } = useUserProfileData();
  const permissions = usePermissions(userProfile as Parameters<typeof usePermissions>[0]);

  // Perfil aún cargando: spinner en lugar de un frame en blanco.
  if (isLoading) {
    return <Loading message="Verificando permisos" />;
  }

  // No user profile available
  if (!userProfile) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check permissions using the provided function
  const hasPermission = permissionCheck(permissions);

  if (!hasPermission) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}